import React, { useState, useEffect } from "react";
import { X, ChevronDown, FileText, Download } from "lucide-react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { showSuccessToast, showErrorToast } from "../toastmsg";
import StatusBadge from "./StatusBadge";
import { getCandidateFields } from "../formSchemas/candidateSchemas";

interface CandidateFormProps {
  candidateId: string;
  onClose: () => void;
  onSave?: (updatedCandidate: Record<string, any>) => void;
}

const SideCandidateForm: React.FC<CandidateFormProps> = ({
  candidateId,
  onClose,
  onSave,
}) => {
  const [candidate, setCandidate] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDetails, setShowDetails] = useState(true);
  const [resumeURL, setResumeURL] = useState<string | null>(null);

  const fields = getCandidateFields([]);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const docRef = doc(db, "job-application", candidateId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setCandidate(data);

          // ✅ Set resume URL directly from Firestore field
          setResumeURL(data.resumeURL || null);
        } else {
          showErrorToast("Candidate not found.");
        }
      } catch (error) {
        console.error("Error fetching candidate:", error);
        showErrorToast("Failed to fetch candidate data.");
      } finally {
        setLoading(false);
      }
    };

    if (candidateId) fetchCandidate();
  }, [candidateId]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setCandidate((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Validation
  const validateFields = () => {
    const newErrors: Record<string, string> = {};
    if (!candidate.name || !/^[A-Za-z\s]+$/.test(candidate.name)) {
      newErrors.name = "Name must only contain letters and spaces.";
    }
    if (!/^(\+?60|0)1\d{8,9}$/.test(candidate.phoneNumber || "")) {
      newErrors.phoneNumber = "Invalid Malaysian phone number.";
    }
    if (
      candidate.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.email)
    ) {
      newErrors.email = "Invalid email address.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Save candidate updates
  const handleSave = async () => {
    if (!validateFields()) return;
    try {
      const docRef = doc(db, "job-application", candidateId);
      await updateDoc(docRef, candidate);
      showSuccessToast("Candidate updated successfully.");
      setIsEditing(false);
      if (onSave) onSave(candidate);
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to update candidate.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading candidate...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-gray-500 w-[500px] h-screen overflow-auto">
      {/* Header */}
      <div className="fixed top-0 w-[500px] py-2 px-4 flex justify-between items-center bg-blue-500 text-white z-10">
        <h3 className="text-lg font-bold">{candidate.name || "Candidate"}</h3>
        <X onClick={onClose} className="w-6 h-6 cursor-pointer" />
      </div>

      <div className="px-4 pt-20 pb-10">
        {/* Candidate summary */}
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-xl font-bold">{candidate.name}</h4>
          <StatusBadge status={candidate.status} size="lg" />
        </div>

        <div className="text-sm space-y-2 mb-4">
          <p>
            <strong>Phone:</strong> {candidate.phoneNumber || "-"}
          </p>
          <p>
            <strong>Email:</strong> {candidate.email || "-"}
          </p>
          <p>
            <strong>Position:</strong> {candidate.position || "-"}
          </p>
          <p>
            <strong>Qualification:</strong> {candidate.qualification || "-"}
          </p>
        </div>

        {/* 🔹 Resume Section */}
        <div className="border-t border-gray-400 pt-4 mb-6">
          <h4 className="font-bold mb-2 flex items-center gap-2">
            <FileText className="w-5 h-5" /> Resume
          </h4>
          {resumeURL ? (
            <div className="flex gap-4">
              <button
                onClick={() => window.open(resumeURL, "_blank")}
                className="py-1 px-3 bg-green-600 text-white rounded hover:bg-green-700"
              >
                View PDF
              </button>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No resume uploaded.</p>
          )}
        </div>

        {/* Editable Details */}
        <div className="border-t border-gray-400 pt-4">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex gap-2 items-center"
            >
              <ChevronDown
                className={`transition-transform duration-200 ${
                  showDetails ? "rotate-180" : ""
                }`}
              />
              <h4 className="font-bold">Details</h4>
            </button>
            {!isEditing && (
              <button
                className="text-blue-500 underline"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            )}
          </div>

          {showDetails && (
            <form className="flex flex-col gap-4">
              {fields.map((field) => (
                <div key={field.name} className="flex flex-col">
                  <label className="text-sm font-medium mb-1">
                    {field.label}
                  </label>

                  {field.type === "select" ? (
                    <select
                      name={field.name}
                      value={candidate[field.name] || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`border border-gray-500 rounded p-1 ${
                        !isEditing ? "bg-gray-200" : ""
                      }`}
                    >
                      <option value="">Select</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      name={field.name}
                      value={candidate[field.name] || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`border border-gray-500 rounded p-1 ${
                        !isEditing ? "bg-gray-200" : ""
                      }`}
                    />
                  )}

                  {errors[field.name] && isEditing && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              ))}

              {isEditing && (
                <div className="flex gap-4 mt-4">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setErrors({});
                    }}
                    className="w-full py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideCandidateForm;
